/*
  Sahla storefront behaviours on top of Sense.
  Cart mutations go through Shopify's cart API with the Section Rendering API so the drawer is always
  rendered by Liquid. The product page is driven by Sense's product-info.js / product-form.js; this file syncs
  the Sahla gallery and sticky bar to variant changes and to the Pumper "Buy more, save more" selection.
*/
(() => {
  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];
  const focusable = (root) =>
    $$('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', root).filter(
      (el) => !el.hidden && el.offsetParent !== null
    );
  let returnFocus = null;
  const anyLayerOpen = () => $('.sahla-menu-drawer.is-open') || $('.sahla-modal.is-open') || $('cart-drawer.is-open');
  const lock = () => document.body.classList.add('drawer-open', 'overflow-hidden');
  const unlock = () => { if (!anyLayerOpen()) document.body.classList.remove('drawer-open', 'overflow-hidden'); };
  const inEditor = () => !!(window.Shopify && window.Shopify.designMode);
  /* Per-section cleanup so editor re-renders never pile up observers, timers or listeners. */
  const sectionCleanups = new Map();
  const onSectionUnload = (el, fn) => { const section = el.closest('.shopify-section') || el; const list = sectionCleanups.get(section) || []; list.push(fn); sectionCleanups.set(section, list); };
  document.addEventListener('shopify:section:unload', (e) => { (sectionCleanups.get(e.target) || []).forEach((fn) => { try { fn(); } catch (err) {} }); sectionCleanups.delete(e.target); });
  const handleize = (value) => String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  /* ---------- Menu drawer ---------- */
  const setMenu = (on, trigger = null) => {
    const drawer = $('[data-menu-drawer]');
    const overlay = $('[data-menu-overlay]');
    if (!drawer || !overlay) return;
    const opener = $('[data-open-menu]');
    if (on) {
      document.querySelector('cart-drawer')?.close?.();
      returnFocus = trigger || document.activeElement;
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      opener?.setAttribute('aria-expanded', 'true');
      lock();
      requestAnimationFrame(() => (focusable(drawer)[0] || drawer).focus());
    } else {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      opener?.setAttribute('aria-expanded', 'false');
      unlock();
      if (returnFocus?.isConnected) returnFocus.focus();
    }
  };

  /* ---------- Bottom-sheet modals ---------- */
  const openModal = (modal, trigger = null) => {
    if (!modal) return;
    $$('.sahla-modal.is-open').forEach((other) => { if (other !== modal) closeModal(other); });
    returnFocus = trigger || document.activeElement;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    lock();
    requestAnimationFrame(() => (focusable(modal)[0] || modal.querySelector('[role="dialog"]'))?.focus());
  };
  const closeModal = (modal) => {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    unlock();
    if (returnFocus?.isConnected) returnFocus.focus();
  };
  const activeLayer = () => $('.sahla-modal.is-open') || $('.sahla-menu-drawer.is-open');
  document.addEventListener('keydown', (e) => {
    const layer = activeLayer();
    if (!layer) return;
    if (e.key === 'Escape') { e.preventDefault(); if (layer.classList.contains('sahla-modal')) closeModal(layer); else setMenu(false); return; }
    if (e.key !== 'Tab') return;
    const items = focusable(layer);
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0]; const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  document.addEventListener('sahla:cart:open', () => { if ($('.sahla-menu-drawer.is-open')) setMenu(false); });

  /* ---------- Toast ---------- */
  function showToast(message, isError = false) {
    const toast = $('[data-cart-toast]');
    if (!toast) return;
    toast.querySelector('span').textContent = message;
    toast.classList.toggle('is-error', !!isError);
    toast.querySelector('button')?.toggleAttribute('hidden', !!isError);
    toast.classList.add('is-visible');
    clearTimeout(showToast.timer);
    showToast.timer = setTimeout(() => toast.classList.remove('is-visible'), isError ? 4200 : 2400);
  }

  /* ---------- Cart API ---------- */
  const cartDrawer = () => document.querySelector('cart-drawer');
  const cartRoutes = () => (window.routes || {});
  const sectionIds = () => (cartDrawer()?.getSectionsToRender?.() || []).map((s) => s.id);
  /* ---------- Pumper discounts outside the product page ----------
     Pumper's discount function only fires when the cart line carries a "__pumper_bundles" property with the offer id.
     On product pages Pumper's own script adds it; elsewhere (homepage cards, quick-add, cart cross-sell) the theme adds it. */
  const pumperOfferCache = {};
  function pumperOffersFromSettings() {
    const map = {};
    String(window.Sahla?.pumperOffersRaw || '').split(/\r?\n/).forEach((line) => { const parts = line.split(':'); if (parts.length >= 2) { const handle = parts[0].trim().toLowerCase(); const id = parts.slice(1).join(':').trim(); if (handle && id) map[handle] = id; } });
    return map;
  }
  async function resolvePumperOffer(handle) {
    if (!handle) return null;
    if (handle in pumperOfferCache) return pumperOfferCache[handle];
    const fromSettings = pumperOffersFromSettings()[handle];
    if (fromSettings) { pumperOfferCache[handle] = fromSettings; return fromSettings; }
    if (window.pumper?.offer?.offerId && String(window.pumper.offer.productUrl || '').includes(`/products/${handle}`)) { pumperOfferCache[handle] = window.pumper.offer.offerId; return pumperOfferCache[handle]; }
    try { const cached = sessionStorage.getItem(`sahla-pumper-${handle}`); if (cached !== null) { pumperOfferCache[handle] = cached || null; return pumperOfferCache[handle]; } } catch (err) {}
    let id = '';
    try {
      const html = await (await fetch(`/products/${handle}`, { headers: { Accept: 'text/html' } })).text();
      const match = html.match(/offerId(?:&quot;|")\s*:\s*(?:&quot;|")([A-Za-z0-9_-]+)/);
      id = match ? match[1] : '';
    } catch (err) {}
    try { sessionStorage.setItem(`sahla-pumper-${handle}`, id); } catch (err) {}
    pumperOfferCache[handle] = id || null;
    return pumperOfferCache[handle];
  }
  /* Fold duplicate lines (same variant, same properties; Pumper's "e2bdf8", "e2bdf8.0" and "e2bdf8:1:x" count as the same offer) into one line.
     Runs after every add, including Pumper's own product-page adds. Shopify changes every line key after each edit, so lines are
     addressed by position and the cart is re-read before every step. */
  let mergeRun = null; let mergeAgain = false;
  const normLine = (line) => { const props = Object.assign({}, line.properties || {}); if (props.__pumper_bundles) props.__pumper_bundles = String(props.__pumper_bundles).split(/[.:]/)[0]; return `${line.variant_id}|${JSON.stringify(Object.keys(props).sort().map((k) => [k, props[k]]))}`; };
  function mergeCartLines() {
    if (mergeRun) { mergeAgain = true; return mergeRun; }
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const getCart = async () => (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    const change = (body) => fetch(cartRoutes().cart_change_url || '/cart/change.js', { method: 'POST', headers, body: JSON.stringify(body) });
    mergeRun = (async () => {
      let changed = false;
      for (let guard = 0; guard < 12; guard++) {
        const cart = await getCart();
        const seen = new Map(); let dupIndex = -1; let key = '';
        (cart.items || []).some((line, i) => { const k = normLine(line); if (seen.has(k)) { dupIndex = i; key = k; return true; } seen.set(k, i); return false; });
        if (dupIndex < 0) break;
        const dup = cart.items[dupIndex];
        const removed = await change({ line: dupIndex + 1, quantity: 0 }).catch(() => null);
        if (!removed || !removed.ok) break;
        changed = true;
        const after = await getCart();
        const survivorIndex = (after.items || []).findIndex((line) => normLine(line) === key);
        if (survivorIndex < 0) break;
        const bumped = await change({ line: survivorIndex + 1, quantity: after.items[survivorIndex].quantity + dup.quantity }).catch(() => null);
        if (!bumped || !bumped.ok) break;
      }
      return changed;
    })().catch(() => false).then((changed) => { mergeRun = null; if (mergeAgain) { mergeAgain = false; return mergeCartLines().then((again) => changed || again); } return changed; });
    return mergeRun;
  }
  async function applyPumperOffer(addResponse) {
    const added = addResponse?.items || (addResponse?.handle ? [addResponse] : []);
    const handles = [...new Set(added.map((item) => item.handle).filter(Boolean))];
    if (!handles.length) return false;
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const getCart = async () => (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    const offers = {};
    for (const handle of handles) { const id = await resolvePumperOffer(handle); if (id) offers[handle] = id; }
    let changed = false;
    /* Shopify changes every line key after each cart edit, so the cart is re-read before every step. */
    for (let guard = 0; guard < 12; guard++) {
      const cart = await getCart();
      const untagged = (cart.items || []).find((line) => offers[line.handle] && !(line.properties && line.properties.__pumper_bundles));
      if (!untagged) break;
      const properties = Object.assign({}, untagged.properties || {}, { __pumper_bundles: offers[untagged.handle] });
      const res = await fetch(cartRoutes().cart_change_url || '/cart/change.js', { method: 'POST', headers, body: JSON.stringify({ id: untagged.key, quantity: untagged.quantity, properties }) }).catch(() => null);
      if (!res || !res.ok) break;
      changed = true;
    }
    const merged = await mergeCartLines();
    return changed || merged;
  }
  async function addToCart(payload, { open = true, handle = null } = {}) {
    const drawer = cartDrawer();
    /* Tag the line with Pumper's offer up front so identical adds merge into one line and the discount applies. */
    const offerId = handle ? await resolvePumperOffer(handle).catch(() => null) : null;
    const tag = offerId || null; /* same value Pumper writes on product pages, so Shopify merges identical adds itself */
    const config = { method: 'POST', headers: { Accept: 'application/javascript' } };
    if (payload instanceof FormData) {
      if (drawer) { payload.append('sections', sectionIds().join(',')); payload.append('sections_url', window.location.pathname); }
      if (tag && !payload.has('properties[__pumper_bundles]')) payload.append('properties[__pumper_bundles]', tag);
      config.body = payload;
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
    } else {
      const base = Array.isArray(payload) ? payload : [{ id: Number(payload), quantity: 1 }];
      const items = base.map((item) => (tag && !(item.properties && item.properties.__pumper_bundles)) ? Object.assign({}, item, { properties: Object.assign({}, item.properties || {}, { __pumper_bundles: tag }) }) : item);
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify({ items, sections: sectionIds(), sections_url: window.location.pathname });
    }
    const response = await fetch(cartRoutes().cart_add_url || '/cart/add', config);
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add to cart');
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sahla-quick-add', productVariantId: data.variant_id || data.items?.[0]?.variant_id, cartData: data });
    }
    /* Queue the tagging/merge before the drawer renders, so the drawer can wait for it instead of showing a line that then folds away. */
    queueCartWork(() => applyPumperOffer(data)).catch(() => {}).then(() => { drawer?.refresh?.(); setTimeout(() => drawer?.refresh?.(), 1500); });
    if (drawer) { drawer.setActiveElement?.(document.activeElement); if (open) drawer.renderContents(data); else drawer.replaceFromSections(data.sections); }
    showToast(window.Sahla?.texts?.addedToast || 'Added to cart');
    return data;
  }
  /* All cart edits made by the theme (Pumper tagging, duplicate merging) run one after another, never at the same time. */
  let cartWork = Promise.resolve();
  function queueCartWork(fn) { const run = cartWork.then(fn, fn); cartWork = run.catch(() => {}); return run; }
  const busy = (el, on) => { if (!el) return; el.disabled = on; if (on) el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy'); };
  window.SahlaCart = { add: addToCart, open: () => cartDrawer()?.open(), close: () => cartDrawer()?.close(), refresh: () => cartDrawer()?.refresh(), toast: showToast, settled: () => cartWork };

  /* ---------- Card variant buttons (cards only; the PDP uses Sense's variant-selects) ---------- */
  function selectCardVariant(button) {
    const form = button.closest('form.sahla-card-form');
    if (!form) return;
    $$('[data-variant-button]', form).forEach((b) => { b.classList.remove('is-active'); b.setAttribute('aria-pressed', 'false'); });
    button.classList.add('is-active');
    button.setAttribute('aria-pressed', 'true');
    const id = $('[name="id"]', form);
    if (id) id.value = button.dataset.variantId;
    const card = form.closest('[data-product-card]');
    const price = card?.querySelector('[data-card-price]');
    if (price) price.innerHTML = (button.dataset.compare ? `<s class="sahla-pcard__compare">${button.dataset.compare}</s> ` : '') + (button.dataset.price || '');
    const available = button.dataset.available === 'true';
    const addButton = $('[data-add-button]', form);
    if (addButton) { addButton.disabled = !available; const label = addButton.querySelector('span') || addButton; label.textContent = available ? addButton.dataset.addLabel || 'Add to cart' : window.variantStrings?.soldOut || 'Sold out'; }
    if (button.dataset.image) { const image = card?.querySelector('[data-card-main-image]'); if (image) { image.src = button.dataset.image; image.removeAttribute('srcset'); } }
    if (card && button.dataset.color) { const accent = $('[data-card-accent]', card); if (accent) accent.style.background = button.dataset.color; }
  }

  /* ---------- Pumper "Buy more, save more" → sticky bar price and shades (labels stay as they are) ---------- */
  const pumperState = () => {
    const checked = document.querySelector('input.pumper_pair:checked, #pumper_bundle_svelte input[name="cb"]:checked');
    if (!checked) return null;
    const qty = Math.max(1, Number(checked.value) || 1);
    const tier = checked.closest('.pumper_block__cb') || checked.parentElement;
    const priceEl = tier?.querySelector('[id^="pumper_totalAmount_"]');
    const shades = tier ? [...tier.querySelectorAll('input[type="hidden"][class*="pumper_selector"]')].map((i) => i.value).filter(Boolean) : [];
    return { qty, price: priceEl ? priceEl.textContent.trim() : '', shades };
  };
  function syncStickyToPumper(info) {
    if (!info) return;
    const sticky = info.querySelector('[data-sticky-product]');
    if (!sticky) return;
    const state = pumperState();
    const price = sticky.querySelector('[data-sticky-price]');
    const variantEl = sticky.querySelector('[data-sticky-variant]');
    if (!state || state.qty <= 1) {
      if (price) price.textContent = state?.price || info.dataset.unitPriceText || price.textContent;
      if (variantEl && info.dataset.variantTitle !== undefined) variantEl.textContent = info.dataset.variantTitle;
      return;
    }
    if (price && state.price) price.textContent = state.price;
    if (variantEl) variantEl.textContent = state.shades.length ? state.shades.join(' + ') : `${state.qty} × ${info.dataset.variantTitle || ''}`.trim();
  }
  const pumperInfos = new Set();
  let pumperListenersReady = false;
  let pumperTimer = null;
  const schedulePumper = () => { clearTimeout(pumperTimer); pumperTimer = setTimeout(() => pumperInfos.forEach((info) => { if (info.isConnected) syncStickyToPumper(info); else pumperInfos.delete(info); }), 80); };
  function initPumperSync(info) {
    if (!info || info.dataset.pumperSync === 'true') return;
    info.dataset.pumperSync = 'true';
    pumperInfos.add(info);
    if (!pumperListenersReady) {
      pumperListenersReady = true;
      document.addEventListener('change', (e) => { if (e.target.closest('#pumper_bundle_svelte, .pumper-bundles, [class*="pumper"]')) schedulePumper(); });
      document.addEventListener('click', (e) => { if (e.target.closest('#pumper_bundle_svelte, .pumper-bundles')) schedulePumper(); });
    }
    let tries = 0; let observer = null; let pollTimer = null;
    const attach = () => {
      if (!info.isConnected) return;
      const root = document.querySelector('#pumper_bundle_svelte, .pumper-bundles');
      if (root) {
        observer = new MutationObserver(schedulePumper);
        observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['checked', 'class', 'value'] });
        schedulePumper();
        return;
      }
      if (tries++ < 40) pollTimer = setTimeout(attach, 500);
    };
    attach();
    onSectionUnload(info, () => { observer?.disconnect(); clearTimeout(pollTimer); pumperInfos.delete(info); });
  }

  /* ---------- Click delegation ---------- */
  document.addEventListener('click', async (e) => {
    const menuOpen = e.target.closest('[data-open-menu]');
    if (menuOpen) { e.preventDefault(); setMenu(true, menuOpen); return; }
    const menuClose = e.target.closest('[data-close-menu],[data-menu-overlay]');
    if (menuClose) { e.preventDefault(); setMenu(false); return; }
    const cartOpen = e.target.closest('[data-open-cart]');
    if (cartOpen) { e.preventDefault(); cartDrawer()?.open(cartOpen); return; }
    const removeCode = e.target.closest('[data-cart-discount-remove]');
    if (removeCode) {
      e.preventDefault();
      busy(removeCode, true);
      try {
        const current = await (await fetch('/cart.js')).json();
        const codes = (current.discount_codes || []).map((d) => d.code).filter((c) => c.toLowerCase() !== removeCode.dataset.cartDiscountRemove.toLowerCase());
        const response = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: codes.join(','), sections: sectionIds().join(','), sections_url: window.location.pathname }) });
        const data = await response.json().catch(() => ({}));
        const drawer = cartDrawer();
        if (drawer && data.sections) drawer.replaceFromSections(data.sections);
        drawer?.refresh?.();
      } catch (err) { showToast(err.message, true); }
      return;
    }

    const presetBundle = e.target.closest('[data-add-preset-bundle]');
    if (presetBundle) {
      e.preventDefault();
      if (presetBundle.disabled) return;
      const card = presetBundle.closest('.sahla-preset-bundle');
      const source = $('[data-preset-bundle-items]', card);
      let items = [];
      try { items = JSON.parse(source?.textContent || '[]'); } catch (err) {}
      if (!items.length) return;
      busy(presetBundle, true);
      try { await addToCart(items, { open: true }); } catch (err) { showToast(err.message, true); } finally { busy(presetBundle, false); }
      return;
    }
    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      const id = Number(quick.dataset.quickAdd);
      if (!id || quick.disabled) return;
      busy(quick, true);
      try { const modal = quick.closest('.sahla-modal'); if (modal) closeModal(modal); await addToCart(id, { open: true, handle: quick.dataset.productHandle || null }); }
      catch (err) { showToast(err.message, true); }
      finally { if (quick.isConnected) busy(quick, false); }
      return;
    }
    const variantButton = e.target.closest('[data-variant-button]');
    if (variantButton) { e.preventDefault(); selectCardVariant(variantButton); return; }

    const stickyAdd = e.target.closest('[data-add-current]');
    if (stickyAdd) {
      e.preventDefault();
      const page = stickyAdd.closest('.sahla-product-page');
      const form = page?.querySelector('form.sahla-product-form');
      const submit = form?.querySelector('[type="submit"]');
      if (!form || stickyAdd.disabled) return;
      if (submit && !submit.disabled) submit.click(); /* real click so Pumper (which intercepts the theme button) adds the selected tier */
      return;
    }
    const buy = e.target.closest('[data-buy-now]');
    if (buy) {
      e.preventDefault();
      const form = buy.closest('form.sahla-product-form, form.sahla-card-form');
      if (!form || buy.disabled) return;
      if (inEditor()) { showToast('Buy now is disabled inside the theme editor'); return; }
      busy(buy, true);
      try {
        const data = new FormData(form);
        const state = pumperState();
        if (state && state.qty > 1 && !state.shades.length) data.set('quantity', String(state.qty));
        const response = await fetch(cartRoutes().cart_add_url || '/cart/add', { method: 'POST', body: data, headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' } });
        const json = await response.json().catch(() => ({}));
        if (!response.ok || json.status) throw new Error(json.description || json.message || 'Unable to add to cart');
        await queueCartWork(() => applyPumperOffer(json)).catch(() => {});
        window.location.href = window.Sahla?.routes?.checkout || '/checkout';
      } catch (err) { showToast(err.message, true); busy(buy, false); }
      return;
    }
    const thumb = e.target.closest('[data-gallery-thumb]');
    if (thumb) { e.preventDefault(); activateDesktopMedia(thumb.closest('[data-gallery]'), thumb.dataset.mediaId); return; }
    const arrow = e.target.closest('[data-desktop-gallery-prev], [data-desktop-gallery-next]');
    if (arrow) {
      e.preventDefault(); e.stopPropagation();
      const gallery = arrow.closest('[data-gallery]');
      const items = $$('[data-desktop-main]', gallery).filter((el) => !el.classList.contains('is-shade-hidden'));
      if (items.length < 2) return;
      const current = Math.max(0, items.findIndex((el) => el.classList.contains('is-active')));
      const delta = arrow.hasAttribute('data-desktop-gallery-prev') ? -1 : 1;
      const target = items[(current + delta + items.length) % items.length];
      activateDesktopMedia(gallery, target.dataset.galleryMedia);
      return;
    }

    const deskArrow = e.target.closest('[data-desktop-gallery-prev],[data-desktop-gallery-next]');
    if (deskArrow) {
      e.preventDefault();
      const gallery = deskArrow.closest('[data-gallery]');
      const items = [...gallery.querySelectorAll('[data-desktop-main]')].filter((item) => !item.classList.contains('is-shade-hidden'));
      if (items.length < 2) return;
      const current = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
      const step = deskArrow.hasAttribute('data-desktop-gallery-next') ? 1 : -1;
      const next = items[(current + step + items.length) % items.length];
      if (next) activateDesktopMedia(gallery, next.dataset.galleryMedia);
      return;
    }
    const play = e.target.closest('[data-toggle-media]');
    if (play) {
      const video = play.closest('[data-media-card]')?.querySelector('video');
      if (video) { if (video.paused) { video.play().catch(() => {}); play.classList.add('is-playing'); } else { video.pause(); play.classList.remove('is-playing'); } }
      return;
    }
    const filter = e.target.closest('[data-shop-filter]');
    if (filter) {
      e.preventDefault();
      const section = filter.closest('[data-shop-section]');
      $$('[data-shop-filter]', section).forEach((b) => { b.classList.remove('is-active'); b.setAttribute('aria-pressed', 'false'); });
      filter.classList.add('is-active'); filter.setAttribute('aria-pressed', 'true');
      const keywords = (filter.dataset.shopFilter || 'all').toLowerCase().split(',').map((k) => k.trim()).filter(Boolean);
      $$('[data-product-card]', section).forEach((card) => { const categories = (card.dataset.category || '').toLowerCase(); card.style.display = keywords.includes('all') || keywords.some((k) => categories.includes(k)) ? '' : 'none'; });
      return;
    }
    const tab = e.target.closest('[data-tab-target]');
    if (tab) {
      e.preventDefault();
      const section = tab.closest('[data-category-tabs]');
      if (!section) return;
      $$('[data-tab-target]', section).forEach((t) => { const on = t === tab; t.classList.toggle('is-active', on); t.setAttribute('aria-selected', on ? 'true' : 'false'); });
      $$('[data-tab-panel]', section).forEach((panel) => { const on = panel.dataset.tabPanel === tab.dataset.tabTarget; panel.classList.toggle('is-active', on); panel.hidden = false; panel.setAttribute('aria-hidden', on ? 'false' : 'true'); });
      return;
    }
    const reviewsNav = e.target.closest('[data-reviews-prev],[data-reviews-next]');
    if (reviewsNav) {
      e.preventDefault();
      const track = reviewsNav.closest('[data-reviews-carousel]')?.querySelector('[data-reviews-track]');
      const card = track?.querySelector('.sahla-review-card:not([hidden])');
      if (track && card) track.scrollBy({ left: (card.offsetWidth + 14) * (reviewsNav.hasAttribute('data-reviews-next') ? 1 : -1), behavior: 'smooth' });
      return;
    }
    const openLook = e.target.closest('[data-open-look]');
    if (openLook) { e.preventDefault(); openModal(document.getElementById(openLook.dataset.openLook), openLook); return; }
    const routineBuilder = e.target.closest('[data-open-routine-builder]');
    if (routineBuilder) { e.preventDefault(); closeModal(routineBuilder.closest('.sahla-modal')); const target = document.getElementById('sets'); if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' }); else if (routineBuilder.dataset.href && !inEditor()) window.location.href = routineBuilder.dataset.href; return; }
    const openShades = e.target.closest('[data-open-shades]');
    if (openShades) { e.preventDefault(); openModal(document.getElementById(openShades.dataset.openShades), openShades); return; }
    const modalVariant = e.target.closest('[data-modal-variant]');
    if (modalVariant) {
      e.preventDefault();
      const modal = modalVariant.closest('.sahla-shade-modal');
      const card = modal?.closest('[data-product-card]');
      const target = card?.querySelector(`form.sahla-card-form [data-variant-button][data-variant-id="${modalVariant.dataset.modalVariant}"]`);
      if (target) { if (target.hidden) { $$('form.sahla-card-form [data-variant-button]', card).forEach((b) => (b.hidden = false)); card.querySelector('.sahla-more-shades')?.setAttribute('hidden', ''); } selectCardVariant(target); }
      closeModal(modal);
      return;
    }
    const modalClose = e.target.closest('[data-close-modal]');
    if (modalClose) { e.preventDefault(); closeModal(modalClose.closest('.sahla-modal')); }
  });

  /* ---------- Forms ---------- */
  document.addEventListener('submit', async (e) => {
    const discount = e.target.closest('[data-cart-discount-form]');
    if (discount) {
      e.preventDefault();
      const input = discount.querySelector('[name="discount"]');
      const code = (input?.value || '').trim();
      if (!code) return;
      const drawer = cartDrawer();
      const message = drawer?.querySelector('[data-cart-discount-message]');
      const button = discount.querySelector('button');
      busy(button, true);
      try {
        const current = await (await fetch('/cart.js')).json();
        const codes = (current.discount_codes || []).map((d) => d.code).filter((c) => c.toLowerCase() !== code.toLowerCase());
        codes.push(code);
        const response = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: codes.join(','), sections: sectionIds().join(','), sections_url: window.location.pathname }) });
        const data = await response.json().catch(() => ({}));
        const applied = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
        if (!response.ok || !applied) throw new Error(window.Sahla?.texts?.discountInvalid || 'This code cannot be applied');
        if (!applied.applicable) throw new Error(window.Sahla?.texts?.discountNotApplicable || 'This code does not apply to your cart');
        if (drawer && data.sections) drawer.replaceFromSections(data.sections);
        drawer?.refresh?.();
        showToast(window.Sahla?.texts?.discountApplied || 'Discount applied');
      } catch (err) {
        if (message) { message.textContent = err.message; message.hidden = false; }
        else showToast(err.message, true);
      } finally { busy(button, false); }
      return;
    }
    const cardForm = e.target.closest('form.sahla-card-form');
    if (!cardForm) return;
    e.preventDefault();
    const submit = e.submitter || cardForm.querySelector('[type="submit"]');
    if (submit?.disabled) return;
    busy(submit, true); submit?.classList.add('loading'); submit?.querySelector('.loading__spinner')?.classList.remove('hidden');
    try { await addToCart(new FormData(cardForm), { open: true, handle: cardForm.dataset.productHandle || null }); }
    catch (err) { showToast(err.message, true); }
    finally { busy(submit, false); submit?.classList.remove('loading'); submit?.querySelector('.loading__spinner')?.classList.add('hidden'); }
  });

  /* ---------- Product gallery ---------- */
  function activateDesktopMedia(gallery, mediaId) {
    if (!gallery || !mediaId) return;
    $$('[data-gallery-thumb]', gallery).forEach((button) => { const active = button.dataset.mediaId === String(mediaId); button.classList.toggle('is-active', active); button.setAttribute('aria-current', active ? 'true' : 'false'); });
    $$('.sahla-gallery-main [data-gallery-media]', gallery).forEach((item) => { const active = item.dataset.galleryMedia === String(mediaId); item.classList.toggle('is-active', active); item.hidden = !active; const video = item.querySelector('video'); if (!video) return; if (active) { video.muted = true; video.play().catch(() => {}); } else video.pause(); });
  }
  function initMobileGallery(gallery) {
    if (gallery.dataset.mobileGalleryReady === 'true') return;
    gallery.dataset.mobileGalleryReady = 'true';
    let timer; let current = 0;
    const wrap = gallery.parentElement;
    const slides = () => [...gallery.children].filter((s) => !s.hidden);
    const thumbs = () => $$('[data-mobile-gallery-thumb]', wrap).filter((t) => !t.hidden);
    const prev = $('[data-mobile-gallery-prev]', wrap);
    const next = $('[data-mobile-gallery-next]', wrap);
    if (!slides().length) return;
    const setActive = (index) => {
      const list = slides(); if (!list.length) return;
      current = (index + list.length) % list.length;
      const th = thumbs();
      th.forEach((thumb, i) => { const active = i === current; thumb.classList.toggle('is-active', active); thumb.setAttribute('aria-current', String(active)); });
      const activeThumb = th[current];
      if (activeThumb) { const strip = activeThumb.parentElement; if (strip && strip.scrollWidth > strip.clientWidth + 2) strip.scrollTo({ left: activeThumb.offsetLeft - strip.clientWidth / 2 + activeThumb.offsetWidth / 2, behavior: 'smooth' }); }
      list.forEach((slide, i) => { const video = slide.querySelector('video'); if (!video) return; if (i === current) { video.muted = true; video.play().catch(() => {}); } else video.pause(); });
    };
    const go = (index) => { setActive(index); const slide = slides()[current]; if (slide) gallery.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' }); };
    const nearest = () => {
      const box = gallery.getBoundingClientRect(); const center = box.left + box.width / 2;
      let index = 0; let distance = Infinity;
      slides().forEach((slide, i) => { const rect = slide.getBoundingClientRect(); const d = Math.abs(rect.left + rect.width / 2 - center); if (d < distance) { distance = d; index = i; } });
      setActive(index);
    };
    wrap.addEventListener('click', (e) => { const thumb = e.target.closest('[data-mobile-gallery-thumb]'); if (!thumb) return; const index = thumbs().indexOf(thumb); if (index >= 0) go(index); });
    prev?.addEventListener('click', () => go(current - 1));
    next?.addEventListener('click', () => go(current + 1));
    gallery.addEventListener('scroll', () => { clearTimeout(timer); timer = setTimeout(nearest, 100); }, { passive: true });
    gallery.goToMedia = (mediaId) => { const index = slides().findIndex((slide) => slide.dataset.galleryMedia === String(mediaId)); if (index >= 0) go(index); };
    gallery.refreshSlides = () => { const list = slides(); const arrows = $('.sahla-mobile-gallery-arrows', wrap); if (arrows) arrows.hidden = list.length < 2; go(0); };
    /* Once the page has loaded, fetch the remaining slides of the visible gallery so swiping never lands on a blank image. */
    const prefetchRest = () => { const root = gallery.closest('[data-gallery]') || wrap; root.querySelectorAll('.sahla-gallery-item img[loading="lazy"]').forEach((img) => { const item = img.closest('.sahla-gallery-item'); const box = item && item.hasAttribute('data-desktop-main') ? item.parentElement : item; if (box && box.getClientRects().length) img.loading = 'eager'; }); };
    if (document.readyState === 'complete') setTimeout(prefetchRest, 1200); else window.addEventListener('load', () => setTimeout(prefetchRest, 1200), { once: true });
    setActive(0);
  }
  function applyShadeFilter(gallery, shade) {
    if (!gallery || gallery.dataset.filterShades !== 'true') return;
    const key = handleize(shade);
    const items = $$('[data-shades]', gallery);
    if (!key || !items.length) return;
    const shadesOf = (el) => (el.dataset.shades || 'all').split(/\s+/);
    const anySpecific = items.some((el) => shadesOf(el).includes(key));
    items.forEach((el) => { const list = shadesOf(el); const show = !anySpecific || list.includes('all') || list.includes(key); el.classList.toggle('is-shade-hidden', !show); if (!el.hasAttribute('data-desktop-main')) el.hidden = !show; });
    const activeDesk = gallery.querySelector('[data-desktop-main].is-active');
    if (!activeDesk || activeDesk.classList.contains('is-shade-hidden')) { const first = gallery.querySelector('[data-desktop-main]:not(.is-shade-hidden)'); if (first) activateDesktopMedia(gallery, first.dataset.galleryMedia); }
    const mobile = gallery.querySelector('[data-mobile-gallery]');
    if (mobile?.refreshSlides) mobile.refreshSlides();
  }
  function showMedia(gallery, mediaId) {
    if (!gallery || !mediaId) return;
    activateDesktopMedia(gallery, mediaId);
    const mobile = gallery.querySelector('[data-mobile-gallery]');
    if (mobile?.goToMedia) mobile.goToMedia(mediaId);
  }
  if (!customElements.get('media-gallery')) {
    customElements.define('media-gallery', class SahlaMediaGallery extends HTMLElement { setActiveMedia(mediaId) { showMedia(this, String(mediaId || '').split('-').pop()); } });
  }

  /* ---------- Variant change from Sense's product-info ---------- */
  function onVariantChange(event) {
    const data = event?.data;
    if (!data) return;
    const info = document.querySelector(`product-info[data-section="${data.sectionId}"]`) || document.querySelector('product-info');
    if (!info) return;
    const variant = data.variant;
    const freshSticky = data.html?.querySelector?.('[data-sticky-product]');
    if (variant) {
      info.dataset.variantTitle = variant.title !== 'Default Title' ? variant.title : '';
      if (freshSticky) info.dataset.unitPriceText = freshSticky.querySelector('[data-sticky-price]')?.textContent || '';
    }
    const sticky = info.querySelector('[data-sticky-product]');
    if (sticky) {
      const stickyPrice = sticky.querySelector('[data-sticky-price]');
      const stickyVariant = sticky.querySelector('[data-sticky-variant]');
      if (stickyPrice && info.dataset.unitPriceText) stickyPrice.textContent = info.dataset.unitPriceText;
      if (stickyVariant) stickyVariant.textContent = info.dataset.variantTitle || '';
      const stickyButton = sticky.querySelector('[data-add-current]');
      if (stickyButton) stickyButton.disabled = !(variant && variant.available);
    }
    const buyNow = info.querySelector('[data-buy-now]');
    if (buyNow) buyNow.disabled = !(variant && variant.available);
    const gallery = info.querySelector('[data-gallery]');
    if (variant?.option1) applyShadeFilter(gallery, variant.option1);
    if (variant?.featured_media?.id) showMedia(gallery, variant.featured_media.id);
    setTimeout(() => syncStickyToPumper(info), 150);
  }

  /* ---------- Keep the page still when an app (Pumper) programmatically selects a shade ----------
     Selecting a shade inside Pumper syncs the theme's shade radio; the browser would scroll that radio into view. */
  let lastPointer = { y: 0, at: 0, inApp: false };
  document.addEventListener('pointerdown', (e) => { lastPointer = { y: window.scrollY, at: Date.now(), inApp: !!e.target.closest('#pumper_bundle_svelte, .pumper-bundles, [class*="pumper"], .sahla-product-app') }; }, { passive: true, capture: true });
  document.addEventListener('focusin', (e) => {
    if (!e.target.matches('.sahla-variant-input')) return;
    if (!lastPointer.inApp || Date.now() - lastPointer.at > 1500) return;
    const y = lastPointer.y;
    window.scrollTo({ top: y });
    requestAnimationFrame(() => window.scrollTo({ top: y }));
    setTimeout(() => window.scrollTo({ top: y }), 60);
  }, true);

  /* ---------- Accordions: only one open per group ---------- */
  document.addEventListener('toggle', (e) => {
    const details = e.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    if (!details.matches('.sahla-detail, .sahla-faq')) return;
    const group = details.parentElement;
    if (!group) return;
    group.querySelectorAll(':scope > details[open]').forEach((other) => { if (other !== details) other.removeAttribute('open'); });
  }, true);

  /* ---------- Delivery estimate ---------- */
  function setDeliveryDates(scope = document) {
    $$('[data-delivery-date]', scope).forEach((el) => {
      try {
        const days = Math.max(1, Number(el.dataset.businessDays) || 3);
        const cutoff = Number(el.dataset.cutoffHour) || 0;
        const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Beirut', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: false }).formatToParts(new Date());
        const part = (type) => Number(parts.find((item) => item.type === type)?.value);
        const date = new Date(Date.UTC(part('year'), part('month') - 1, part('day'), 12));
        let left = days;
        if (cutoff > 0 && part('hour') >= cutoff) left += 1;
        while (left > 0) { date.setUTCDate(date.getUTCDate() + 1); const weekday = date.getUTCDay(); if (weekday !== 0 && weekday !== 6) left -= 1; }
        el.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(date);
      } catch (e) {}
    });
  }

  /* ---------- Sticky bar ---------- */
  /* One shared scroll listener for every sticky bar (editor re-renders used to add a new listener each time). */
  const stickyBars = new Set();
  let stickyScheduled = false;
  const updateStickies = () => {
    stickyScheduled = false;
    stickyBars.forEach((sticky) => {
      if (!sticky.isConnected || !sticky._main?.isConnected) { stickyBars.delete(sticky); return; }
      const visible = sticky._main.getBoundingClientRect().bottom <= 0;
      sticky.classList.toggle('is-visible', visible);
      document.body.classList.toggle('sahla-sticky-visible', visible);
      window.SahlaWhatsApp?.setLifted(visible && window.innerWidth < 900);
      window.SahlaWhatsApp?.liftKlaviyo?.(visible && window.innerWidth < 900);
    });
  };
  const requestStickies = () => { if (!stickyScheduled) { stickyScheduled = true; requestAnimationFrame(updateStickies); } };
  window.addEventListener('scroll', requestStickies, { passive: true });
  window.addEventListener('resize', requestStickies);
  function initStickyProduct(sticky) {
    if (sticky.dataset.stickyReady === 'true') return;
    sticky.dataset.stickyReady = 'true';
    const info = sticky.closest('.sahla-product-page');
    const main = $('[data-main-add]', info);
    if (!main) return;
    info.dataset.unitPriceText = sticky.querySelector('[data-sticky-price]')?.textContent || '';
    info.dataset.variantTitle = sticky.querySelector('[data-sticky-variant]')?.textContent || '';
    sticky._main = main;
    stickyBars.add(sticky);
    updateStickies();
  }

  /* ---------- Header: hide on scroll down, show on scroll up (mobile) ---------- */
  function initHeaderScroll() {
    const header = $('.sahla-header');
    if (!header) return;
    const bar = $('.sahla-announcement[data-announcement-sticky="true"]');
    const setBarHeight = () => document.documentElement.style.setProperty('--sahla-announcement-height', `${bar ? bar.offsetHeight : 0}px`);
    setBarHeight();
    window.addEventListener('resize', setBarHeight);
    let lastY = window.scrollY; let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY;
      const isMobile = window.innerWidth < 900;
      const goingDown = y > lastY + 4;
      const goingUp = y < lastY - 4;
      if (!isMobile || y < 80) document.body.classList.remove('sahla-header-hidden');
      else if (goingDown && !anyLayerOpen()) document.body.classList.add('sahla-header-hidden');
      else if (goingUp) document.body.classList.remove('sahla-header-hidden');
      lastY = y;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  }

  /* ---------- Product page: keep the top rating identical to the Judge.me carousel header once it renders ---------- */
  function initRatingSync(info) {
    if (!info || info.dataset.ratingSync === 'true') return;
    info.dataset.ratingSync = 'true';
    const target = info.querySelector('.sahla-pdp-rating .sahla-rating');
    if (!target) return;
    const apply = () => {
      const header = document.querySelector('.sahla-product-reviews-section .jdgm-header .jdgm-rating-text, .sahla-product-reviews-section .jdgm-rating-text');
      if (!header) return false;
      const countEl = header.querySelector('.jdgm-reviews-count');
      const ratingMatch = header.textContent.match(/(\d+(?:[.,]\d+)?)/);
      const count = countEl ? Number(countEl.textContent.replace(/\D/g, '')) : NaN;
      const rating = ratingMatch ? Number(ratingMatch[1].replace(',', '.')) : NaN;
      if (!rating || !count) return false;
      const value = target.querySelector('.sahla-rating__value');
      if (value) value.innerHTML = `${rating.toFixed(2)} <span class="sahla-rating__star" aria-hidden="true">★</span> (${count})`;
      target.querySelectorAll('.sahla-star').forEach((star, i) => star.style.setProperty('--fill', `${Math.max(0, Math.min(100, Math.round((rating - i) * 100)))}%`));
      target.setAttribute('aria-label', `Rated ${rating.toFixed(2)} out of 5 based on ${count} reviews`);
      return true;
    };
    if (apply()) return;
    const scope = document.querySelector('.sahla-product-reviews-section') || document.body;
    const observer = new MutationObserver(() => { if (apply()) observer.disconnect(); });
    observer.observe(scope, { childList: true, subtree: true });
    const stop = setTimeout(() => observer.disconnect(), 120000);
    onSectionUnload(info, () => { observer.disconnect(); clearTimeout(stop); });
  }

  /* ---------- Every star rating on the site (cards, tabs, product page) refreshed from Judge.me's badge API ----------
     Shopify's product-rating metafields lag behind Judge.me; the badge API returns the same figures as Judge.me's widgets.
     Needs the Judge.me public token (Theme settings → Sahla brand). Results are cached for 10 minutes per product. */
  function paintRating(el, rating, count) {
    if (!el || !rating) return;
    const value = el.querySelector('.sahla-rating__value');
    if (value) value.innerHTML = `${rating.toFixed(2)} <span class="sahla-rating__star" aria-hidden="true">★</span> (${count})`;
    el.querySelectorAll('.sahla-star').forEach((star, i) => star.style.setProperty('--fill', `${Math.max(0, Math.min(100, Math.round((rating - i) * 100)))}%`));
    el.setAttribute('aria-label', `Rated ${rating.toFixed(2)} out of 5 based on ${count} reviews`);
  }
  async function fetchJudgemeBadge(handle) {
    if (!handle) return null;
    const key = `sahla-jdgm-${handle}`;
    try { const cached = JSON.parse(sessionStorage.getItem(key) || 'null'); if (cached && Date.now() - cached.at < 600000) return cached; } catch (err) {}
    let result = null;
    /* 1. The product page: Judge.me renders its carousel header (score + count) server-side, so it is the exact source of truth. */
    try {
      const html = await (await fetch(`/products/${handle}`, { headers: { Accept: 'text/html' } })).text();
      const header = html.match(/jdgm-rating-text[^>]*>\s*([\d.,]+)[\s\S]{0,200}?jdgm-reviews-count[^>]*>\s*(\d+)/);
      if (header) { const rating = Number(header[1].replace(',', '.')); const count = Number(header[2]); if (rating && count) result = { rating, count, at: Date.now() }; }
    } catch (err) {}
    /* 2. Judge.me badge API, when a public token is set in Theme settings. */
    const token = window.Sahla?.judgemeToken;
    const shop = window.Sahla?.shopDomain;
    if (!result && token && shop) {
      try {
        const response = await fetch(`https://judge.me/api/v1/widgets/preview_badge?shop_domain=${encodeURIComponent(shop)}&api_token=${encodeURIComponent(token)}&handle=${encodeURIComponent(handle)}`);
        if (response.ok) {
          const json = await response.json();
          const badge = json.badge || json.html || '';
          const rating = Number((badge.match(/data-average-rating=['"]([\d.]+)/) || [])[1]);
          const count = Number((badge.match(/data-number-of-reviews=['"](\d+)/) || [])[1]);
          if (rating && count) result = { rating, count, at: Date.now() };
        }
      } catch (err) {}
    }
    if (result) { try { sessionStorage.setItem(key, JSON.stringify(result)); } catch (err) {} }
    return result;
  }  function initJudgemeRatingSync(scope = document) {
    const byHandle = {};
    $$('[data-rating-sync][data-product-handle]', scope).forEach((el) => { const h = el.dataset.productHandle; if (!h) return; (byHandle[h] = byHandle[h] || []).push(el); });
    Object.entries(byHandle).forEach(async ([handle, els]) => { const data = await fetchJudgemeBadge(handle); if (data) els.forEach((el) => paintRating(el, data.rating, data.count)); });
  }

  /* ---------- Homepage reviews: copy Judge.me carousel reviews into the theme's own cards ---------- */
  const STAR_PATH = 'M12 2.6l2.9 6.1 6.7.8-4.9 4.6 1.3 6.6L12 17.4l-6 3.3 1.3-6.6L2.4 9.5l6.7-.8z';
  const starMarkup = (fill) => `<span class="sahla-star" style="--fill:${fill}%"><svg viewBox="0 0 24 24"><path d="${STAR_PATH}"/></svg><svg class="sahla-star__fill" viewBox="0 0 24 24"><path d="${STAR_PATH}"/></svg></span>`;
  function initJudgemeReviews(section) {
    if (!section || section.dataset.judgemeReady === 'true') return;
    section.dataset.judgemeReady = 'true';
    const carousel = section.querySelector('[data-reviews-carousel][data-reviews-text-source="judgeme"]');
    const source = section.querySelector('[data-reviews-source]');
    if (!carousel || !source) return;
    const track = carousel.querySelector('[data-reviews-track]');
    const template = carousel.querySelector('[data-review-card-template]');
    const max = Math.max(1, Number(carousel.dataset.reviewsMax) || 8);
    const fallback = () => { carousel.classList.remove('is-loading'); carousel.classList.add('is-fallback'); };
    const build = () => {
      const items = [...source.querySelectorAll('.jdgm-carousel-item, .jdgm-rev')].filter((el) => !el.dataset.cloneType && !el.classList.contains('jdgm-clone-before') && !el.classList.contains('jdgm-clone-after'));
      const seen = new Set();
      const reviews = [];
      items.forEach((el) => {
        const text = (el.querySelector('.jdgm-text, [class*="review-body"], .jdgm-rev__body')?.textContent || '').trim();
        const name = (el.querySelector('.jdgm-reviewer-name, [class*="reviewer"], .jdgm-rev__author')?.textContent || '').trim();
        const title = (el.querySelector('.jdgm-title, [class*="review-title"], .jdgm-rev__title')?.textContent || '').trim();
        const on = el.querySelectorAll('.jdgm-star.jdgm--on').length;
        const half = el.querySelectorAll('.jdgm-star.jdgm--half').length;
        const scored = el.querySelector('[data-score]')?.dataset.score;
        const rating = scored ? Number(scored) : on + half * 0.5;
        const photoEl = [...el.querySelectorAll('img')].find((img) => !/star|icon|avatar/i.test(img.className + ' ' + (img.src || '')) && (img.currentSrc || img.src || img.dataset.src));
        const bgMatch = el.querySelector('[style*="background-image"]')?.style.backgroundImage.match(/url\(["']?([^"')]+)/);
        const photo = photoEl ? (photoEl.currentSrc || photoEl.dataset.src || photoEl.src) : (bgMatch ? bgMatch[1] : '');
        const key = el.dataset.reviewId || `${name}|${text.slice(0, 40)}`;
        if (!text || seen.has(key)) return;
        seen.add(key);
        reviews.push({ text, name, title, photo, rating: rating || 5 });
      });
      if (!reviews.length) return false;
      track.querySelectorAll('[data-review-judgeme]').forEach((el) => el.remove());
      reviews.slice(0, max).forEach((review) => {
        const card = template.content.firstElementChild.cloneNode(true);
        card.querySelector('[data-review-stars]').innerHTML = [1, 2, 3, 4, 5].map((i) => starMarkup(Math.max(0, Math.min(100, Math.round((review.rating - i + 1) * 100))))).join('');
        const titleEl = card.querySelector('[data-review-title]');
        if (review.title) { titleEl.textContent = review.title; titleEl.hidden = false; }
        card.querySelector('[data-review-text]').textContent = review.text;
        card.querySelector('[data-review-name]').textContent = review.name || 'Verified buyer';
        const photoWrap = card.querySelector('[data-review-photo]');
        if (review.photo && photoWrap) { photoWrap.querySelector('img').src = review.photo; photoWrap.hidden = false; card.classList.add('has-photo'); }
        track.appendChild(card);
      });
      carousel.classList.remove('is-loading', 'is-fallback');
      carousel.classList.add('is-ready');
      return true;
    };
    if (build()) return;
    let done = false;
    const observer = new MutationObserver(() => { if (build()) { done = true; observer.disconnect(); } });
    observer.observe(source, { childList: true, subtree: true });
    setTimeout(() => { if (!done && !build()) { observer.disconnect(); fallback(); } }, 9000);
  }

  /* ---------- WhatsApp floating button: light lilac bubble, white logo, lifted above the sticky bar ----------
     The eazeapps "WhatsApp button" embed renders inside a <gty-whatsapp-chat-button> shadow root, so the theme injects
     a stylesheet into that root. A generic fallback covers other WhatsApp apps that render in the normal DOM. */
  function styleWhatsAppHost(host) {
    const root = host.shadowRoot;
    if (!root || root.querySelector('[data-sahla-whatsapp-style]')) return;
    const lilac = window.Sahla?.colors?.lilac || '#F1E2F5';
    const plum = window.Sahla?.colors?.plum || '#563A63';
    const style = document.createElement('style');
    style.setAttribute('data-sahla-whatsapp-style', '');
    style.textContent = `.gty-wacb-wrapper { transition: bottom .3s ease, transform .3s ease !important; } .gty-wacb-button { background-color: ${plum} !important; box-shadow: 0 10px 26px rgba(48, 39, 51, .18) !important; } .gty-wacb-button svg path { fill: ${lilac} !important; } :host(.sahla-lifted) .gty-wacb-wrapper { bottom: 96px !important; } .gty-wacb-button, .gty-wacb-button svg path { transition: background-color .3s ease, fill .3s ease; } :host(.sahla-inverted) .gty-wacb-button { background-color: ${lilac} !important; } :host(.sahla-inverted) .gty-wacb-button svg path { fill: ${plum} !important; }`;
    root.appendChild(style);
    paintWhatsAppHost(host, host.classList.contains('sahla-inverted'));
  }
  /* Inline colours with !important as well, in case the app re-renders its own inline styles after our stylesheet. */
  function paintWhatsAppHost(host, inverted) {
    const root = host.shadowRoot; if (!root) return;
    const lilac = window.Sahla?.colors?.lilac || '#F1E2F5';
    const plum = window.Sahla?.colors?.plum || '#563A63';
    const bubble = root.querySelector('.gty-wacb-button'); if (!bubble) return;
    bubble.style.setProperty('background-color', inverted ? lilac : plum, 'important');
    bubble.querySelectorAll('svg path').forEach((p) => p.style.setProperty('fill', inverted ? plum : lilac, 'important'));
  }
  /* Swap the bubble colours when it floats over a plum (dark) background so it never disappears. */
  const parseRgb = (value) => { const m = String(value || '').match(/rgba?\(([^)]+)\)/); if (!m) return null; const parts = m[1].split(',').map((n) => parseFloat(n)); if (parts.length > 3 && parts[3] === 0) return null; return parts; };
  const isDarkColor = (rgb) => rgb && (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000 < 140;
  function backgroundIsDarkAt(x, y, ignore) {
    const stack = document.elementsFromPoint(x, y).filter((el) => !ignore.includes(el) && !el.closest('gty-whatsapp-chat-button, [data-sahla-whatsapp], .sahla-stickybar, .sahla-cart-toast'));
    for (const el of stack) {
      const cs = getComputedStyle(el);
      const rgb = parseRgb(cs.backgroundColor);
      if (rgb) return isDarkColor(rgb);
      if (cs.backgroundImage && cs.backgroundImage !== 'none') return false;
      if (el.tagName === 'IMG' || el.tagName === 'VIDEO') return false;
    }
    return false;
  }
  function watchWhatsAppBackground() {
    let ticking = false; let lastCheck = 0; let forceNext = false;
    const check = () => {
      ticking = false; lastCheck = performance.now(); const force = forceNext; forceNext = false;
      $$('gty-whatsapp-chat-button, [data-sahla-whatsapp]').forEach((host) => {
        const bubble = host.shadowRoot ? host.shadowRoot.querySelector('.gty-wacb-button') : host;
        if (!bubble) return;
        const r = bubble.getBoundingClientRect();
        if (!r.width || r.bottom > window.innerHeight + 4 || r.top < -4) return;
        const dark = backgroundIsDarkAt(r.left + r.width / 2, r.top + r.height / 2, [host, bubble]) || backgroundIsDarkAt(r.left + 4, r.bottom - 4, [host, bubble]);
        const state = dark ? 'dark' : 'light';
        if (!force && host.dataset.sahlaBg === state) return; /* repaint only when the background under the bubble changes */
        host.dataset.sahlaBg = state;
        host.classList.toggle('sahla-inverted', dark);
        if (host.shadowRoot) paintWhatsAppHost(host, dark);
        if (!host.shadowRoot) {
          const lilac = window.Sahla?.colors?.lilac || '#F1E2F5';
          const plum = window.Sahla?.colors?.plum || '#563A63';
          host.style.setProperty('background-color', dark ? lilac : plum, 'important');
          host.querySelectorAll('svg, svg path').forEach((el) => el.style.setProperty('fill', dark ? plum : lilac, 'important'));
        }
      });
    };
    const request = (force) => { if (force) forceNext = true; if (ticking) return; ticking = true; const wait = Math.max(0, 120 - (performance.now() - lastCheck)); setTimeout(() => requestAnimationFrame(check), wait); };
    window.addEventListener('scroll', () => request(false), { passive: true });
    window.addEventListener('resize', () => request(true));
    setInterval(() => { if (!document.hidden) request(true); }, 1500);
    request();
  }
  window.SahlaWhatsApp = {
    setLifted(on) { $$('gty-whatsapp-chat-button, [data-sahla-whatsapp]').forEach((el) => el.classList.toggle('sahla-lifted', !!on)); },
    /* Inline !important beats the apps' own stylesheets (Klaviyo's reset CSS wins over any theme rule). */
    setHidden(on) { $$('gty-whatsapp-chat-button, [data-sahla-whatsapp], [class*="kl-teaser-"]').forEach((el) => { if (on) el.style.setProperty('display', 'none', 'important'); else el.style.removeProperty('display'); }); },
    /* Klaviyo teaser: lifted above the sticky bar on phones. Its drop-shadow filter is replaced by a box-shadow because iOS Safari
       misplaces fixed elements that carry a filter when the browser toolbar collapses (the bubble ended up half under the toolbar). */
    liftKlaviyo(on) {
      $$('[class*="kl-teaser-"]').forEach((el) => {
        if (el.offsetHeight > 120) { el.style.removeProperty('transform'); return; }
        if (el.dataset.sahlaTeaser !== 'true') { el.dataset.sahlaTeaser = 'true'; el.style.setProperty('filter', 'none', 'important'); const inner = el.querySelector('span'); if (inner) inner.style.setProperty('box-shadow', '0 6px 18px rgba(0, 0, 0, .28)', 'important'); }
        if (on) el.style.setProperty('transform', 'translateY(-76px)', 'important'); else el.style.removeProperty('transform');
      });
    },
  };
  /* On phones the cart drawer covers the screen: hide the WhatsApp bubble while it is open. */
  document.addEventListener('sahla:cart:open', () => { if (window.innerWidth < 900) window.SahlaWhatsApp.setHidden(true); });
  document.addEventListener('sahla:cart:close', () => window.SahlaWhatsApp.setHidden(false));
  function styleWhatsApp() {
    $$('gty-whatsapp-chat-button').forEach(styleWhatsAppHost);
    const links = $$('a[href*="wa.me"], a[href*="api.whatsapp.com"], a[href^="whatsapp:"]').filter((a) => !a.closest('.sahla-footer-v2, .sahla-menu-drawer, .sahla-contact-card, .sahla-contact-layout, main, .sahla-page'));
    links.forEach((link) => {
      let fixed = null;
      let node = link;
      while (node && node !== document.body) { if (getComputedStyle(node).position === 'fixed') { fixed = node; break; } node = node.parentElement; }
      if (!fixed || fixed.dataset.sahlaWhatsapp === 'true') return;
      fixed.dataset.sahlaWhatsapp = 'true';
      const lilac = window.Sahla?.colors?.lilac || '#F1E2F5';
      const plum = window.Sahla?.colors?.plum || '#563A63';
      [fixed, link, ...link.querySelectorAll('*:not(svg):not(path):not(circle):not(g)')].forEach((el) => {
        const bg = getComputedStyle(el).backgroundColor;
        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') el.style.setProperty('background-color', plum, 'important');
        if (getComputedStyle(el).backgroundImage !== 'none' && el.tagName !== 'IMG') el.style.setProperty('background-image', 'none', 'important');
      });
      link.querySelectorAll('svg, svg path, svg circle, svg g').forEach((el) => { el.style.setProperty('fill', lilac, 'important'); if (getComputedStyle(el).stroke !== 'none') el.style.setProperty('stroke', lilac, 'important'); });
      link.style.setProperty('color', lilac, 'important');
    });
  }
  function initWhatsApp() {
    styleWhatsApp();
    watchWhatsAppBackground();
    const observer = new MutationObserver(() => { clearTimeout(initWhatsApp.timer); initWhatsApp.timer = setTimeout(styleWhatsApp, 200); });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 30000);
  }

  /* ---------- Product image lightbox with zoom (click / double-tap / pinch / wheel) ---------- */
  const lightbox = { el: null, img: null, items: [], index: 0, scale: 1, x: 0, y: 0, dragging: false, pointers: new Map(), pinchStart: 0, pinchScale: 1, lastTap: 0 };
  function ensureLightbox() {
    if (lightbox.el) return lightbox.el;
    const el = document.createElement('div');
    el.className = 'sahla-lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Product image');
    el.innerHTML = '<div class="sahla-lightbox__stage" data-lightbox-stage><img class="sahla-lightbox__img" alt="" draggable="false" decoding="async"><video class="sahla-lightbox__video" muted loop playsinline autoplay preload="auto" hidden></video></div>'
      + '<button type="button" class="sahla-lightbox__close" data-lightbox-close aria-label="Close">✕</button>'
      + '<button type="button" class="sahla-lightbox__nav sahla-lightbox__nav--prev" data-lightbox-prev aria-label="Previous image"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14.5 6-6 6 6 6"/></svg></button>'
      + '<button type="button" class="sahla-lightbox__nav sahla-lightbox__nav--next" data-lightbox-next aria-label="Next image"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9.5 6 6 6-6 6"/></svg></button>'
      + '<p class="sahla-lightbox__hint">Click or pinch to zoom</p>';
    document.body.appendChild(el);
    lightbox.el = el;
    lightbox.img = el.querySelector('img');
    lightbox.video = el.querySelector('video');
    lightbox.preloaded = new Map();
    const stage = el.querySelector('[data-lightbox-stage]');
    const apply = () => { lightbox.img.style.transform = `translate(${lightbox.x}px, ${lightbox.y}px) scale(${lightbox.scale})`; el.classList.toggle('is-zoomed', lightbox.scale > 1.01); };
    const reset = () => { lightbox.scale = 1; lightbox.x = 0; lightbox.y = 0; apply(); };
    const clamp = () => {
      const r = stage.getBoundingClientRect();
      const maxX = Math.max(0, (lightbox.img.offsetWidth * lightbox.scale - r.width) / 2);
      const maxY = Math.max(0, (lightbox.img.offsetHeight * lightbox.scale - r.height) / 2);
      lightbox.x = Math.max(-maxX, Math.min(maxX, lightbox.x));
      lightbox.y = Math.max(-maxY, Math.min(maxY, lightbox.y));
    };
    const zoomAt = (clientX, clientY, targetScale) => {
      if (lightbox.isVideo) return;
      const r = stage.getBoundingClientRect();
      const cx = clientX - (r.left + r.width / 2);
      const cy = clientY - (r.top + r.height / 2);
      const ratio = targetScale / lightbox.scale;
      lightbox.x = cx - (cx - lightbox.x) * ratio;
      lightbox.y = cy - (cy - lightbox.y) * ratio;
      lightbox.scale = targetScale;
      clamp(); apply();
    };
    const toggleZoom = (clientX, clientY) => { if (lightbox.isVideo) return; if (lightbox.scale > 1.01) reset(); else zoomAt(clientX, clientY, 2.5); };
    /* Full-size images are fetched once and kept warm; the current one and its two neighbours are requested ahead of time. */
    const preload = (item) => { if (!item || item.video || !item.src || lightbox.preloaded.has(item.src)) return lightbox.preloaded.get(item && item.src); const im = new Image(); im.decoding = 'async'; im.src = item.src; const done = new Promise((resolve) => { const ready = () => (im.decode ? im.decode().then(() => resolve(true), () => resolve(true)) : resolve(true)); im.onload = ready; im.onerror = () => resolve(false); if (im.complete && im.naturalWidth) ready(); }); lightbox.preloaded.set(item.src, done); return done; };
    const show = (index) => {
      lightbox.index = (index + lightbox.items.length) % lightbox.items.length;
      const item = lightbox.items[lightbox.index];
      const shown = lightbox.index;
      lightbox.isVideo = !!item.video;
      el.classList.toggle('is-video', lightbox.isVideo);
      if (lightbox.isVideo) {
        lightbox.img.hidden = true;
        lightbox.video.hidden = false;
        if (item.poster) lightbox.video.poster = item.poster;
        if (lightbox.video.getAttribute('src') !== item.video) { lightbox.video.setAttribute('src', item.video); lightbox.video.load(); }
        lightbox.video.muted = true;
        lightbox.video.play().catch(() => {});
      } else {
        lightbox.video.pause();
        lightbox.video.hidden = true;
        lightbox.img.hidden = false;
        lightbox.img.alt = item.alt || '';
        /* Show the gallery's already-loaded picture at once, swap in the large one when it has arrived. */
        if (lightbox.preloaded.has(item.src) && lightbox.img.src !== item.src) lightbox.img.src = item.src;
        if (!lightbox.preloaded.has(item.src)) {
          if (item.small) lightbox.img.src = item.small;
          preload(item).then((ok) => { if (ok && lightbox.index === shown && !lightbox.isVideo) lightbox.img.src = item.src; });
        }
        preload(lightbox.items[(lightbox.index + 1) % lightbox.items.length]);
        preload(lightbox.items[(lightbox.index - 1 + lightbox.items.length) % lightbox.items.length]);
      }
      el.querySelectorAll('[data-lightbox-prev],[data-lightbox-next]').forEach((b) => (b.hidden = lightbox.items.length < 2));
      reset();
    };
    lightbox.show = show;
    /* The gallery behind is moved to the last viewed image only when the expanded view closes, so swiping inside it does no extra work. */
    lightbox.close = () => { el.classList.remove('is-open'); document.body.classList.remove('sahla-lightbox-open'); reset(); lightbox.video.pause(); if (!document.querySelector('cart-drawer.is-open') || window.innerWidth >= 900) window.SahlaWhatsApp?.setHidden(false); if (typeof lightbox.onShow === 'function') { try { lightbox.onShow(lightbox.index); } catch (err) {} } if (lightbox.returnTo?.isConnected) lightbox.returnTo.focus(); };
    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-lightbox-close]')) { lightbox.close(); return; }
      if (e.target.closest('[data-lightbox-prev]')) { show(lightbox.index - 1); return; }
      if (e.target.closest('[data-lightbox-next]')) { show(lightbox.index + 1); return; }
      if (e.target === el || e.target === stage) { lightbox.close(); return; }
    });
    let moved = false;
    stage.addEventListener('pointerdown', (e) => {
      if (e.target !== lightbox.img && e.target !== lightbox.video) return;
      stage.setPointerCapture(e.pointerId);
      lightbox.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      moved = false;
      if (lightbox.pointers.size === 2) { const [a, b] = [...lightbox.pointers.values()]; lightbox.pinchStart = Math.hypot(a.x - b.x, a.y - b.y); lightbox.pinchScale = lightbox.scale; }
      else { lightbox.dragging = lightbox.scale > 1.01; lightbox.dragFrom = { x: e.clientX - lightbox.x, y: e.clientY - lightbox.y }; lightbox.start = { x: e.clientX, y: e.clientY }; }
    });
    stage.addEventListener('pointermove', (e) => {
      if (!lightbox.pointers.has(e.pointerId)) return;
      lightbox.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (lightbox.pointers.size === 2) {
        const [a, b] = [...lightbox.pointers.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const target = Math.max(1, Math.min(4, lightbox.pinchScale * (dist / (lightbox.pinchStart || dist))));
        zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, target);
        moved = true;
      } else if (lightbox.dragging) {
        lightbox.x = e.clientX - lightbox.dragFrom.x; lightbox.y = e.clientY - lightbox.dragFrom.y; clamp(); apply(); moved = true;
      } else if (lightbox.start && (Math.abs(e.clientX - lightbox.start.x) > 12 || Math.abs(e.clientY - lightbox.start.y) > 12)) {
        moved = true; /* swipe in progress (not zoomed) */
      }
    });
    const endPointer = (e) => {
      if (!lightbox.pointers.has(e.pointerId)) return;
      lightbox.pointers.delete(e.pointerId);
      if (lightbox.pointers.size === 0) {
        lightbox.dragging = false;
        if (moved && lightbox.scale <= 1.01 && lightbox.start) {
          const dx = e.clientX - lightbox.start.x; const dy = e.clientY - lightbox.start.y;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(lightbox.index + (dx < 0 ? 1 : -1));
        }
        if (!moved) {
          const now = Date.now();
          if (e.pointerType === 'touch') { if (now - lightbox.lastTap < 320) { toggleZoom(e.clientX, e.clientY); lightbox.lastTap = 0; } else { lightbox.lastTap = now; } }
          else toggleZoom(e.clientX, e.clientY);
        }
      }
    };
    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', endPointer);
    stage.addEventListener('wheel', (e) => { e.preventDefault(); const target = Math.max(1, Math.min(4, lightbox.scale * (e.deltaY < 0 ? 1.15 : 0.87))); zoomAt(e.clientX, e.clientY, target); }, { passive: false });
    document.addEventListener('keydown', (e) => { if (!el.classList.contains('is-open')) return; if (e.key === 'Escape') lightbox.close(); if (e.key === 'ArrowLeft') show(lightbox.index - 1); if (e.key === 'ArrowRight') show(lightbox.index + 1); });
    return el;
  }
  function openLightbox(items, index, returnTo, onShow) {
    if (!items.length) return;
    const el = ensureLightbox();
    lightbox.items = items; lightbox.returnTo = returnTo; lightbox.onShow = onShow || null;
    el.classList.add('is-open');
    document.body.classList.add('sahla-lightbox-open');
    window.SahlaWhatsApp?.setHidden(true); /* WhatsApp and Klaviyo bubbles never float over the expanded view */
    lightbox.show(index);
    el.querySelector('[data-lightbox-close]').focus();
  }
  document.addEventListener('click', (e) => {
    const item = e.target.closest('.sahla-gallery-item[data-zoom-src], .sahla-gallery-item[data-zoom-video]');
    if (!item || e.target.closest('button, a')) return;
    const gallery = item.closest('[data-gallery]');
    const group = item.closest('.sahla-gallery-main') || item.closest('[data-mobile-gallery]') || gallery;
    /* Desktop main images are hidden except the active one, so only the shade filter decides which ones the lightbox cycles through. */
    const siblings = [...group.querySelectorAll('.sahla-gallery-item[data-zoom-src], .sahla-gallery-item[data-zoom-video]')].filter((el) => !el.classList.contains('is-shade-hidden') && (el.hasAttribute('data-desktop-main') || !el.hidden));
    /* The placeholder is the picture the gallery already shows (in cache), so the expanded view opens with no network wait. */
    const items = siblings.map((el) => ({ src: el.dataset.zoomSrc, small: el.querySelector('img')?.currentSrc || el.dataset.zoomSmall || '', alt: el.querySelector('img')?.alt || '', video: el.dataset.zoomVideo || '', poster: el.dataset.zoomPoster || '' }));
    const follow = (i) => { const el = siblings[i]; if (el && el.dataset.galleryMedia) showMedia(gallery, el.dataset.galleryMedia); };
    openLightbox(items, Math.max(0, siblings.indexOf(item)), item, follow);
  });

  /* ---------- Init ---------- */
  let variantUnsubscribe = null; let cartUpdateUnsubscribe = null;
  function initCommerce(scope = document) {
    $$('form.sahla-card-form', scope).forEach((form) => { if (form.dataset.commerceReady === 'true') return; form.dataset.commerceReady = 'true'; const active = $('[data-variant-button].is-active', form) || $('[data-variant-button]', form); if (active) selectCardVariant(active); });
    $$('[data-mobile-gallery]', scope).forEach(initMobileGallery);
    /* Gallery videos autoplay muted; only the visible one should run. */
    $$('.sahla-gallery-main [data-desktop-main][hidden] video', scope).forEach((video) => video.pause());
    $$('media-gallery[data-filter-shades="true"]', scope).forEach((gallery) => applyShadeFilter(gallery, gallery.dataset.currentShade));
    setDeliveryDates(scope);
    $$('[data-sticky-product]', scope).forEach(initStickyProduct);
    $$('product-info', scope).forEach(initPumperSync);
    $$('product-info', scope).forEach(initRatingSync);
    $$('[data-home-reviews]', scope).forEach(initJudgemeReviews);
    initJudgemeRatingSync(scope);
    if (!variantUnsubscribe && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    /* Pumper lets Sense's product form do the add (it only stamps the line), and Sense announces it here: merge duplicate shade lines afterwards. */
    if (!cartUpdateUnsubscribe && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') cartUpdateUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const source = event?.source || '';
      if (source === 'sahla-quick-add' || source === 'cart-items' || source === 'sahla-merge') return;
      queueCartWork(() => mergeCartLines()).then((changed) => { if (changed) cartDrawer()?.refresh?.(); }).catch(() => {});
    });
  }
  document.addEventListener('DOMContentLoaded', () => { initCommerce(); initHeaderScroll(); initWhatsApp(); });
  document.addEventListener('shopify:section:load', (e) => initCommerce(e.target));
})();

/* Header announcement rotation (one-at-a-time mode). */
(() => {
  const initAnnouncement = (root) => {
    if (!root || root.dataset.announcementReady === 'true') return;
    root.dataset.announcementReady = 'true';
    const items = [...root.querySelectorAll('[data-announcement-item]')];
    if (items.length < 2 || root.dataset.autoRotate !== 'true') return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const interval = Math.max(3, Number(root.dataset.interval) || 5) * 1000;
    let index = 0; let timer = null;
    const show = (next) => { index = (next + items.length) % items.length; items.forEach((item, i) => { const active = i === index; item.classList.toggle('is-active', active); item.setAttribute('aria-hidden', String(!active)); }); };
    const start = () => { if (reduce || document.hidden) return; clearInterval(timer); timer = setInterval(() => show(index + 1), interval); };
    const stop = () => { clearInterval(timer); timer = null; };
    root.addEventListener('mouseenter', stop); root.addEventListener('mouseleave', start); root.addEventListener('focusin', stop); root.addEventListener('focusout', start);
    root.addEventListener('shopify:block:select', (event) => { const item = event.target.closest('[data-announcement-item]'); const selected = items.indexOf(item); if (selected >= 0) { stop(); show(selected); } });
    root.addEventListener('shopify:block:deselect', start);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    start();
  };
  const initAll = (scope = document) => scope.querySelectorAll('[data-announcement-bar]').forEach(initAnnouncement);
  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

/* UGC carousel: circular, continuous scaling that follows the scroll, autoplay only while visible. */
(() => {
  const initCarousel = (root) => {
    if (!root || root.dataset.ugcReady === 'true') return;
    root.dataset.ugcReady = 'true';
    const viewport = root.querySelector('[data-ugc-viewport]');
    const track = root.querySelector('[data-ugc-track]');
    const originals = [...root.querySelectorAll('[data-ugc-card]')];
    const prev = root.querySelector('[data-ugc-prev]');
    const next = root.querySelector('[data-ugc-next]');
    if (!viewport || !track || !originals.length) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const seconds = Math.max(3, Number(root.dataset.autoplaySeconds) || 8);
    const total = originals.length;
    const cloneCount = Math.min(3, total);
    let inView = false; let timer = null; let pauseTimer = null; let pauseUntil = 0; let scrollTimer = null; let observer = null; let scaleFrame = null;
    let activePhysical = cloneCount + Math.min(total - 1, window.innerWidth >= 900 ? 2 : 0);
    let normalizing = false;
    const cleanClone = (clone, logical) => { clone.dataset.ugcClone = 'true'; clone.dataset.ugcLogical = String(logical); clone.removeAttribute('data-shopify-editor-block'); clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id')); return clone; };
    originals.forEach((card, i) => (card.dataset.ugcLogical = String(i)));
    if (total > 1) {
      const firstOriginal = originals[0];
      for (let logical = total - cloneCount; logical < total; logical++) track.insertBefore(cleanClone(originals[logical].cloneNode(true), logical), firstOriginal);
      for (let logical = 0; logical < cloneCount; logical++) track.appendChild(cleanClone(originals[logical].cloneNode(true), logical));
    }
    const cards = [...track.querySelectorAll('[data-ugc-card]')];
    /* Warm only the featured clip and its two neighbours (not the whole carousel) so playback is instant without downloading everything. */
    const warmCard = (card) => { card.querySelectorAll('video').forEach((video) => { if (video.dataset.warmed === 'true') return; video.dataset.warmed = 'true'; video.preload = 'auto'; video.muted = true; video.setAttribute('playsinline', ''); try { video.load(); } catch (e) {} }); };
    /* Cards further than two away drop their buffered video again (aborts downloads, frees memory on phones). */
    const coolCard = (card) => { card.querySelectorAll('video').forEach((video) => { if (video.dataset.warmed !== 'true') return; video.dataset.warmed = 'false'; if (!video.paused) video.pause(); video.preload = 'none'; try { video.load(); } catch (e) {} }); };
    const warmAround = () => { cards.forEach((card, i) => { if (Math.abs(i - activePhysical) > 2) coolCard(card); }); [activePhysical - 1, activePhysical, activePhysical + 1].forEach((i) => { if (cards[i]) warmCard(cards[i]); }); };
    if ('IntersectionObserver' in window) { const warmer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { warmAround(); warmer.disconnect(); } }, { rootMargin: '120% 0px' }); warmer.observe(root); } else { warmAround(); }
    const setPausedIcon = (card, paused) => card.querySelector('[data-ugc-pause]')?.classList.toggle('is-paused', paused);
    const stopMedia = (card) => { const video = card.querySelector('video'); if (video) { if (!video.paused) video.pause(); if (video.currentTime > 0) { try { video.currentTime = 0; } catch (e) {} } setPausedIcon(card, true); } const fallback = card.querySelector('[data-ugc-fallback]'); if (fallback && fallback.dataset.poster) fallback.src = fallback.dataset.poster; };
    const playNow = (card, video) => { video.muted = true; const attempt = () => video.play().then(() => setPausedIcon(card, false)).catch(() => {}); if (video.readyState >= 3) attempt(); else { video.addEventListener('canplay', attempt, { once: true }); attempt(); } };
    const startMedia = (card) => { if (!inView) return; const video = card.querySelector('video'); if (video) { playNow(card, video); card.querySelector('[data-ugc-mute]')?.classList.remove('is-unmuted'); } const fallback = card.querySelector('[data-ugc-fallback]'); if (fallback && fallback.dataset.motion) fallback.src = fallback.dataset.motion; };
    const stopAllMedia = () => cards.forEach(stopMedia);
    const cardLeft = (card) => Math.max(0, card.offsetLeft - (viewport.clientWidth - card.offsetWidth) / 2);
    const centerPhysical = (index, behavior = 'smooth') => { const card = cards[index]; if (!card) return; viewport.scrollTo({ left: cardLeft(card), behavior: reduce ? 'auto' : behavior }); };
    const updateScales = () => {
      scaleFrame = null;
      const box = viewport.getBoundingClientRect(); const center = box.left + box.width / 2;
      const big = window.innerWidth >= 900 ? 0.10 : 0.06;
      cards.forEach((card) => {
        const r = card.getBoundingClientRect();
        const d = Math.abs(r.left + r.width / 2 - center) / Math.max(1, r.width);
        const t = Math.max(0, 1 - Math.min(1, d));
        const eased = t * t * (3 - 2 * t);
        card.style.transform = `scale(${(0.92 + (0.08 + big) * eased).toFixed(4)}) translateY(${(-10 * eased).toFixed(2)}px)`;
        card.style.opacity = (0.62 + 0.38 * eased).toFixed(3);
        card.style.zIndex = eased > 0.6 ? '3' : '1';
      });
    };
    const requestScales = () => { if (!scaleFrame) scaleFrame = requestAnimationFrame(updateScales); };
    const applyFeatured = () => { warmAround(); cards.forEach((card, i) => { const on = i === activePhysical; card.classList.toggle('is-featured', on); card.setAttribute('aria-current', on ? 'true' : 'false'); if (on && inView) startMedia(card); else stopMedia(card); }); requestScales(); };
    const restart = () => { clearInterval(timer); timer = null; if (reduce || total < 2 || document.hidden || !inView || pauseUntil === Infinity) return; timer = setInterval(() => move(1, false), seconds * 1000); };
    /* A visitor who taps or swipes a clip takes over: the carousel stops switching by itself. */
    const pauseAfterInteraction = () => { pauseUntil = Infinity; clearInterval(timer); timer = null; clearTimeout(pauseTimer); };
    const centralIndexForLogical = (logical) => cloneCount + logical;
    const normalizeIfClone = () => { if (total < 2) return; const centralStart = cloneCount; const centralEnd = cloneCount + total - 1; if (activePhysical >= centralStart && activePhysical <= centralEnd) return; const logical = Number(cards[activePhysical]?.dataset.ugcLogical || 0); activePhysical = centralIndexForLogical(logical); normalizing = true; centerPhysical(activePhysical, 'auto'); applyFeatured(); requestAnimationFrame(() => (normalizing = false)); };
    const setPhysical = (index, { scroll = true, behavior = 'smooth', restartTimer = true } = {}) => { activePhysical = Math.max(0, Math.min(cards.length - 1, index)); applyFeatured(); if (scroll) centerPhysical(activePhysical, behavior); if (restartTimer) restart(); };
    const move = (delta, manual = false) => { if (total < 2) return; if (manual) pauseAfterInteraction(); setPhysical(activePhysical + delta, { scroll: true, behavior: 'smooth', restartTimer: true }); clearTimeout(scrollTimer); scrollTimer = setTimeout(normalizeIfClone, 520); };
    const nearestToCenter = () => { if (normalizing) return; const box = viewport.getBoundingClientRect(); const center = box.left + box.width / 2; let best = activePhysical; let bestDistance = Infinity; cards.forEach((card, i) => { const r = card.getBoundingClientRect(); const d = Math.abs(r.left + r.width / 2 - center); if (d < bestDistance) { best = i; bestDistance = d; } }); if (best !== activePhysical) { activePhysical = best; applyFeatured(); } normalizeIfClone(); restart(); };
    prev?.addEventListener('click', () => move(-1, true));
    next?.addEventListener('click', () => move(1, true));
    root.addEventListener('shopify:block:select', (event) => { const card = event.target.closest?.('[data-ugc-card]'); if (!card) return; pauseAfterInteraction(); setPhysical(centralIndexForLogical(Number(card.dataset.ugcLogical || 0)), { scroll: true, behavior: 'smooth', restartTimer: false }); });
    track.addEventListener('click', (e) => { const card = e.target.closest('[data-ugc-card]'); if (!card || e.target.closest('a,button,input,select,textarea')) return; pauseAfterInteraction(); const i = cards.indexOf(card); if (i >= 0 && i !== activePhysical) setPhysical(i, { scroll: true, behavior: 'smooth', restartTimer: true }); });
    root.addEventListener('click', (e) => {
      const mute = e.target.closest('[data-ugc-mute]');
      if (mute) { e.preventDefault(); e.stopPropagation(); pauseAfterInteraction(); const video = mute.closest('[data-ugc-card]')?.querySelector('video'); if (!video) return; video.muted = !video.muted; mute.classList.toggle('is-unmuted', !video.muted); return; }
      const pause = e.target.closest('[data-ugc-pause]');
      if (pause) { e.preventDefault(); e.stopPropagation(); pauseAfterInteraction(); const card = pause.closest('[data-ugc-card]'); const video = card?.querySelector('video'); if (!video) return; if (video.paused) { video.play().catch(() => {}); setPausedIcon(card, false); } else { video.pause(); setPausedIcon(card, true); } }
    });
    viewport.addEventListener('pointerdown', pauseAfterInteraction, { passive: true });
    viewport.addEventListener('wheel', pauseAfterInteraction, { passive: true });
    viewport.addEventListener('scroll', () => { requestScales(); clearTimeout(scrollTimer); scrollTimer = setTimeout(nearestToCenter, 150); }, { passive: true });
    window.addEventListener('resize', requestScales);
    const onVisibilityChange = () => { if (document.hidden) { clearInterval(timer); stopAllMedia(); } else if (inView) { applyFeatured(); restart(); } };
    document.addEventListener('visibilitychange', onVisibilityChange);
    if ('IntersectionObserver' in window) { observer = new IntersectionObserver(([entry]) => { inView = entry.isIntersecting && entry.intersectionRatio >= 0.35; if (inView) { applyFeatured(); restart(); } else { clearInterval(timer); timer = null; stopAllMedia(); } }, { threshold: [0, 0.15, 0.35, 0.55, 0.8] }); observer.observe(root); } else { inView = true; }
    const cleanup = () => { clearInterval(timer); clearTimeout(pauseTimer); clearTimeout(scrollTimer); observer?.disconnect(); document.removeEventListener('visibilitychange', onVisibilityChange); stopAllMedia(); };
    root.closest('.shopify-section')?.addEventListener('shopify:section:unload', cleanup, { once: true });
    requestAnimationFrame(() => requestAnimationFrame(() => { centerPhysical(activePhysical, 'auto'); applyFeatured(); if (inView) restart(); }));
  };
  const initAll = (scope = document) => scope.querySelectorAll('[data-ugc-carousel]').forEach(initCarousel);
  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
